import { useState } from "react";
import { RadioGroup, Field, Label, Radio } from "@headlessui/react";
import { FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";

const downloadTypes = [
  { id: "all", name: "All responses", description: "All of your responses" },
  {
    id: "filtered",
    name: "Filtered responses",
    description: "All responses visible after applying filters",
  },
  {
    id: "selected",
    name: "Selected responses",
    description: "All responses from the selected rows",
  },
];

const fileFormats = [
  {
    id: "csv",
    name: "CSV file",
    description: "Works with Google Sheets and other tools",
  },
  {
    id: "xlsx",
    name: "XLSX file",
    description: "Works with Excel 2007 and newer",
  },
];

export function DownloadResponsesModal({
  totalResponses,
  selectedCount,
  filteredCount,
  onDownload,
  onCancel,
  responses,
  questions,
  selected,
  forceTypeSelected = false,
}) {
  const [selectedType, setSelectedType] = useState(
    forceTypeSelected ? downloadTypes[2] : downloadTypes[0]
  );
  const [selectedFormat, setSelectedFormat] = useState(fileFormats[0]);

  const getResponseCount = (type) => {
    switch (type.id) {
      case "all":
        return totalResponses;
      case "filtered":
        return filteredCount;
      case "selected":
        return selectedCount;
      default:
        return 0;
    }
  };

  const isDisabled = (type) => {
    switch (type.id) {
      case "filtered":
        return !filteredCount;
      case "selected":
        return !selectedCount;
      default:
        return false;
    }
  };

  const getFilteredResponses = () => {
    switch (selectedType.id) {
      case "all":
        return responses;
      case "filtered":
        return responses;
      case "selected":
        return responses.filter((sel) => selected.includes(sel._id));
      default:
        return [];
    }
  };

  const handleDownload = () => {
    const filteredResponses = getFilteredResponses();

    // Create worksheet data
    const wsData = filteredResponses.map((response) => {
      const row = {};
      // Add timestamp
      row["Submitted At"] = new Date(response.submittedAt).toLocaleString();

      // Add answers for each question. Surveys with branching logic can have
      // multiple questions with identical text (one per branch); they share a
      // single column, so merge values instead of letting a later empty
      // answer overwrite an earlier real one.
      questions.forEach((question) => {
        if (question.visible === false) return;
        const value = response.answers[question.id];
        const hasValue = value !== undefined && value !== null && value !== "";
        const existing = row[question.text];
        if (hasValue) {
          row[question.text] = existing ? `${existing}, ${value}` : value;
        } else if (existing === undefined) {
          row[question.text] = "";
        }
      });

      return row;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(wsData);

    // Set column widths
    const colWidths = Object.keys(wsData[0] || {}).map((key) => ({
      wch: Math.max(20, key.length),
    }));
    ws["!cols"] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Responses");

    // Generate filename
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `survey-responses-${timestamp}`;

    // Download file based on selected format
    if (selectedFormat.id === "csv") {
      XLSX.writeFile(wb, `${filename}.csv`, {
        bookType: "csv",
      });
    } else {
      XLSX.writeFile(wb, `${filename}.xlsx`);
    }

    onDownload?.(selectedType.id, selectedFormat.id);
  };

  return (
    <div className="space-y-6 max-h-[85vh] overflow-y-auto">
      <div>
        {/* Response Selection */}
        {!forceTypeSelected && (
          <div className="space-y-4">
            <div className="font-medium text-sm text-gray-700">
              Choose responses to download
            </div>
            <RadioGroup value={selectedType} onChange={setSelectedType}>
              <div className="space-y-3">
                {downloadTypes.map((type) => (
                  <Field key={type.id} value={type}>
                    {({ checked }) => (
                      <div
                        onClick={() =>
                          !isDisabled(type) && setSelectedType(type)
                        }
                        className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border 
                                            ${
                                              checked
                                                ? "border-cyan-600 bg-cyan-50"
                                                : "border-gray-200"
                                            }
                                            ${
                                              isDisabled(type)
                                                ? "opacity-50 cursor-not-allowed"
                                                : ""
                                            }`}
                      >
                        <Radio
                          value={type}
                          className="h-4 w-4 border rounded-full border-gray-300 data-[checked]:border-4 data-[checked]:border-cyan-600"
                        />
                        <div>
                          <Label className="font-medium text-sm">
                            {type.name}
                          </Label>
                          <div className="text-xs text-gray-500">
                            {type.description}
                          </div>
                        </div>
                        <div className="ml-auto text-sm text-gray-500">
                          {getResponseCount(type)} responses
                        </div>
                      </div>
                    )}
                  </Field>
                ))}
              </div>
            </RadioGroup>
          </div>
        )}

        {/* File Format Selection */}
        <div className="space-y-4">
          <div className="font-medium text-sm text-gray-700">File format</div>
          <RadioGroup value={selectedFormat} onChange={setSelectedFormat}>
            <div className="space-y-3">
              {fileFormats.map((format) => (
                <Field key={format.id} value={format}>
                  {({ checked }) => (
                    <div
                      onClick={() => setSelectedFormat(format)}
                      className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border 
                                            ${
                                              checked
                                                ? "border-cyan-600 bg-cyan-50"
                                                : "border-gray-200"
                                            }`}
                    >
                      <Radio
                        value={format}
                        className="h-4 w-4 border rounded-full border-gray-300 data-[checked]:border-4 data-[checked]:border-cyan-600"
                      />
                      <FileSpreadsheet className="h-5 w-5 text-gray-500" />
                      <div>
                        <Label className="font-medium text-sm">
                          {format.name}{" "}
                          <span className="font-thin text-sm text-gray-800">
                            ({getResponseCount(selectedType)} response{getResponseCount(selectedType) !== 1 ? 's' : ''})
                          </span>
                        </Label>
                        <div className="text-xs text-gray-500">
                          {format.description}
                        </div>
                      </div>
                    </div>
                  )}
                </Field>
              ))}
            </div>
          </RadioGroup>
        </div>
      </div>
      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="text-sm text-gray-500">
          {getResponseCount(selectedType)} responses will be downloaded
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
